import type { DataTableRowType } from '#components/elements/dataTable/dataTable.model';
import { translateDimensionCellText } from '#components/elements/dataTable/dataTable.utils';
import { useKpisPerimeter } from '#composables/kpisPerimeter';
import { useFiltersForQueryKey } from '#composables/useFiltersForQueryKey';
import { useLanguage } from '#composables/useLanguage';
import { getDataTableTotalKpi } from '#domain/performance/kpi/kpi.query';
import { withLikeForLikeStoreKeys } from '#domain/performance/kpi/prepare-query-input';
import { useLikeForLikePerimeter } from '#domain/performance/likeForLike/useLikeForLikePerimeter';
import type { DataTableColumn, DataTableKpiDisplayNameType, DataTableViewType } from '@carrefour-gcs/shared';
import { useQuery } from '@tanstack/vue-query';
import { computed, unref, type Ref } from 'vue';
import { formatDataTableRow } from './formatDataTableRow';

/** i18n key the API uses to label the total row's dimension column. */
const TOTAL_DIMENSION_LABEL_KEY = 'dataTablePerformance.dimension.total';

interface UseDataTableTotalRowParams {
  dataTableView: Ref<DataTableViewType>;
  selectedKpis: Ref<DataTableKpiDisplayNameType[] | null>;
  columnsDefinition: Ref<Record<string, DataTableColumn>>;
  canFetchRows: Ref<boolean>;
  excludeFiltersFromQueryKey?: string[];
}

export function useDataTableTotalRow(params: UseDataTableTotalRowParams) {
  const { dataTableView, selectedKpis, columnsDefinition, canFetchRows, excludeFiltersFromQueryKey } = params;

  const { t, language } = useLanguage();

  const {
    taxonomy: { taxonomy },
    withTax: { isWithTax },
    filters: { selectedFiltersKpiQuery },
    period: { selectedPeriod, comparisonPeriod },
    storePerimeter: { storePerimeter },
    myDataIncluded: { isMyDataIncluded },
    detentionPeriod: { detentionPeriod }
  } = useKpisPerimeter();

  const { resolveLikeForLikeStoreKeys } = useLikeForLikePerimeter();

  const excludedFilterKeys = excludeFiltersFromQueryKey ?? [];
  const filtersForQueryKey = useFiltersForQueryKey(selectedFiltersKpiQuery, excludedFilterKeys);

  // Narrow key: intentionally excludes dimensions, sort, limit, offset.
  const queryKey = computed(() => [
    getDataTableTotalKpi.name,
    selectedPeriod.value,
    comparisonPeriod.value,
    filtersForQueryKey.value,
    isWithTax.value,
    isMyDataIncluded.value,
    taxonomy.value,
    storePerimeter.value,
    dataTableView.value,
    selectedKpis.value,
    detentionPeriod.value
  ]);

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: async ({ signal }) => {
      // L4L: the front-resolved perimeter is a data dependency of the request.
      const likeForLikeStoreKeys = await resolveLikeForLikeStoreKeys();

      const eligibleDetentionViews = ['possessionZoomPage', 'possessionZoomPageWithTheoretical', 'performance'];

      const res = await getDataTableTotalKpi(
        {
          period: selectedPeriod.value,
          comparisonPeriod: comparisonPeriod.value,
          ...(eligibleDetentionViews.includes(dataTableView.value ?? '') && { detentionPeriod: detentionPeriod.value }),
          query: {
            filters: withLikeForLikeStoreKeys(
              selectedFiltersKpiQuery.value,
              storePerimeter.value,
              likeForLikeStoreKeys
            ),
            isWithTax: isWithTax.value,
            isMyDataIncluded: isMyDataIncluded.value
          },
          selectedKpis: selectedKpis.value ?? [],
          taxonomy: unref(taxonomy),
          storePerimeter: unref(storePerimeter),
          dataTableView: unref(dataTableView)
        },
        signal
      );

      if (res.status !== 'success') {
        throw new Error(`getDataTableTotalKpi failed: ${res.status}`);
      }

      return res.data.total;
    },
    enabled: canFetchRows
  });

  const totalRow = computed((): DataTableRowType<string> | null => {
    const rawTotal = data.value;
    if (!rawTotal || !columnsDefinition.value) {
      return null;
    }
    const formattedRow = formatDataTableRow({
      row: rawTotal,
      columnsDefinition: columnsDefinition.value,
      t,
      language: language.value,
      dataTableView: dataTableView.value
    });
    // Force Total label
    for (const [column, definition] of Object.entries(columnsDefinition.value)) {
      if (definition?.type === 'dimension') {
        formattedRow[column] = {
          type: 'dimension',
          data: translateDimensionCellText(t, column, [TOTAL_DIMENSION_LABEL_KEY])
        };
      }
    }
    return formattedRow;
  });

  return {
    totalRow,
    isLoadingTotal: isLoading,
    isErrorTotal: isError
  };
}
